import api from './api'

export type MonthUiStatus = 'complete' | 'partial' | 'incomplete'

export type ExpectedReconcilingItems = {
  total: string
  by_store: Array<{
    store: string
    location_id: string
    amount: string
  }>
}

export type StoreBreakdownItem = {
  location_id: string | null
  store: string
  department_id: string | null
  department_name?: string | null
  clover_outstanding: string
  qbo_balance: string
  variance: string
  expected_reconciling_items?: string | null
}

export type MonthSummaryItem = {
  report_month: string
  status: MonthUiStatus
  hint: string | null
  has_upload: boolean
  variance_total?: string
}

export type UploadMetadata = {
  report_month: string
  source_filename: string
  file_content_type: string | null
  report_as_of: string | null
  uploaded_at: string | null
  clover_outstanding_total: string
  clover_outstanding_by_location: Record<string, string> | null
  parsed: Record<string, unknown> | null
  as_of_warning: string | null
}

export type ReconSnapshot = {
  qbo_balance_total: string
  qbo_balances_by_department: Record<string, string>
  qbo_fetched_at: string | null
  variance_total: string
  recon_content_hash: string | null
}

export type MonthDetailResponse = {
  report_month: string
  ui_status: MonthUiStatus
  hint: string | null
  upload: UploadMetadata | null
  recon: ReconSnapshot | null
  stores: StoreBreakdownItem[]
  tied: boolean
  expected_reconciling_items: ExpectedReconcilingItems | null
}

export type UploadResponse = {
  report_month: string
  filename: string
  as_of: string | null
  clover_outstanding_total: string
  clover_outstanding_by_location: Record<string, string>
  warnings: string[]
}

export type ReconcileResponse = {
  report_month: string
  clover_outstanding_total: string
  clover_outstanding_by_location: Record<string, string>
  qbo_balance_total: string
  qbo_balances_by_department: Record<string, string>
  qbo_fetched_at: string
  variance_total: string
  recon_content_hash: string
  warnings: string[]
  stores: StoreBreakdownItem[]
  tied: boolean
  expected_reconciling_items: ExpectedReconcilingItems
}

const multipartHeaders = { headers: { 'Content-Type': 'multipart/form-data' } }

export async function getGiftCardMonthsSummary(): Promise<{ months: MonthSummaryItem[] }> {
  const { data } = await api.get<{ months: MonthSummaryItem[] }>('/gift-cards/months-summary')
  return data
}

export async function getGiftCardMonth(reportMonth: string): Promise<MonthDetailResponse> {
  const { data } = await api.get<MonthDetailResponse>(`/gift-cards/months/${reportMonth}`)
  return data
}

export async function uploadGiftCardReport(
  reportMonth: string,
  file: File,
): Promise<UploadResponse> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<UploadResponse>(
    `/gift-cards/months/${reportMonth}/upload`,
    form,
    multipartHeaders,
  )
  return data
}

export async function fetchGiftCardReportFromClover(
  reportMonth: string,
): Promise<UploadResponse> {
  const { data } = await api.post<UploadResponse>(
    `/gift-cards/months/${reportMonth}/fetch`,
    null,
  )
  return data
}

export async function deleteGiftCardUpload(
  reportMonth: string,
  confirm: boolean,
): Promise<void> {
  await api.delete(`/gift-cards/months/${reportMonth}/upload`, {
    params: confirm ? { confirm: true } : undefined,
  })
}

export async function downloadGiftCardUpload(reportMonth: string): Promise<void> {
  const response = await api.get<Blob>(`/gift-cards/months/${reportMonth}/upload/file`, {
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] as string | undefined
  let filename = `gift-card-report-${reportMonth}.xls`
  if (disposition) {
    const match = /filename="?([^";\n]+)"?/.exec(disposition)
    if (match?.[1]) filename = match[1]
  }
  const url = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export async function reconcileGiftCardMonth(
  reportMonth: string,
  opts?: { force?: boolean },
): Promise<ReconcileResponse> {
  const { data } = await api.post<ReconcileResponse>(
    `/gift-cards/months/${reportMonth}/reconcile`,
    null,
    { params: { force: opts?.force ?? false } },
  )
  return data
}

export async function markGiftCardMonthComplete(
  reportMonth: string,
): Promise<MonthDetailResponse> {
  const { data } = await api.post<MonthDetailResponse>(
    `/gift-cards/months/${reportMonth}/complete`,
    null,
  )
  return data
}
